'use client';

import {
  useId,
  type ChangeEvent,
  type MouseEvent,
  type KeyboardEvent,
  forwardRef,
  useContext,
} from 'react';
import type { CheckboxProps } from './Checkbox.types';
import styles from './Checkbox.module.css';
import { Flex, Icon, InlineUnsafe, Pill, Text } from '@/_components';
import { getClasses } from '@/_utils/classnames';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const Checkbox = forwardRef<HTMLDivElement, CheckboxProps>(
  (
    {
      className,
      onChange,
      isDisabled,
      helperText,
      helperTextColor,
      isRequired,
      isSmall,
      isChecked,
      pillText,
      isLabelHidden,
      label,
      id,
      useCustomTheme,
      withGlossary,
    },
    ref,
  ) => {
    const { glossaries } = useContext(FirmSettingsContext);

    const checkboxId: string = useId();

    const checkboxClasses: string = getClasses(styles.checkbox, className, {
      [styles.checkboxChecked]: isChecked === true,
      [styles.checkboxCheckedPartially]: isChecked === 'indeterminate',
      [styles.checkboxSmall]: isSmall,
      [styles.checkboxDisabled]: isDisabled,
      [styles.checkboxThemed]: useCustomTheme,
    });

    const checkboxLabelClasses: string = getClasses(styles.checkboxLabel);

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.code !== 'Space' && event.code !== 'Enter') {
        return;
      }

      event.preventDefault();

      onChange({
        target: {
          checked: !(isChecked && isChecked !== 'indeterminate'),
        },
      } as ChangeEvent<HTMLInputElement>);
    };

    const onCheckboxChange = (event: MouseEvent<HTMLInputElement>) => {
      event.preventDefault();

      onChange?.({
        target: {
          checked: !(isChecked && isChecked !== 'indeterminate'),
        },
      } as ChangeEvent<HTMLInputElement>);
    };

    return (
      <Flex>
        <Flex ref={ref} isYCentered={true} className={checkboxClasses} gap={4}>
          <input
            aria-checked={isChecked === true}
            aria-required={isRequired}
            required={isRequired}
            type="checkbox"
            className={styles.checkboxInput}
            defaultChecked={isChecked === true}
            onKeyDown={onKeyDown}
            disabled={isDisabled}
            onClick={onCheckboxChange}
            id={checkboxId}
          />
          <Flex isCentered={true} className={styles.checkIcon}>
            <Icon
              name="check-square"
              size={isSmall ? 16 : 24}
              color="currentcolor"
              internalIconWidth="fill"
            />
          </Flex>
          <Flex isCentered={true} className={styles.checkPartiallyIcon}>
            <Icon
              name="minus-square"
              size={isSmall ? 16 : 24}
              color="currentcolor"
            />
          </Flex>
          <label
            htmlFor={checkboxId}
            className={`${checkboxLabelClasses}  ${id || ''}`}
          >
            {!isLabelHidden && (
              <>
                <Text
                  type="small"
                  isYCentered={true}
                  fontWeight="medium"
                  color={isDisabled ? 'disabled' : undefined}
                >
                  <InlineUnsafe
                    text={label}
                    glossaries={withGlossary ? glossaries : undefined}
                  />
                </Text>
                {helperText && (
                  <Text
                    color={
                      isDisabled ? 'disabled' : (helperTextColor ?? 'secondary')
                    }
                    type="tiny"
                  >
                    {helperText}
                  </Text>
                )}
              </>
            )}
          </label>
          {pillText && (
            <Pill label={pillText} intent="secondary" isRound={true} />
          )}
        </Flex>
      </Flex>
    );
  },
);

Checkbox.displayName = 'Checkbox';
